import { useCallback, useEffect, useRef, useState } from 'react';
import type { ErrorDetails } from '#api/errors.ts';
import { PageWorker } from './PageWorker.ts';
import type { Log, LogStatus, LogsWorkerMessage } from './worker/log-worker-types.ts';

export function useLogWorker(hostId: string) {
  const pwRef = useRef<PageWorker | null>(null);
  const getPW = useCallback(
    function getPw() {
      if (pwRef.current !== null) {
        return pwRef.current;
      }

      // We check for browser support of SharedWorker in DebugConsole,
      // and show a fallback UI if necessary.  But we have to avoid the error here, which
      // is called before DebugConsole is rendered.
      if (typeof globalThis.SharedWorker === 'undefined') {
        return null;
      }

      const pw = new PageWorker(
        new SharedWorker(new URL('./worker/log-worker.ts', import.meta.url), {
          type: 'module',
          name: 'Defined Networking debug logs',
        }).port,
        hostId
      );
      pwRef.current = pw;
      return pwRef.current;
    },
    [hostId]
  );

  const [status, setStatus] = useState<LogStatus>();
  const [sessionStart, setSessionStart] = useState<Date | null>(null);
  const [sessionEnd, setSessionEnd] = useState<Date | null>(null);
  const hasRequestedAllLogsRef = useRef(false);
  const [isSubscribed, setSubscribed] = useState(false);
  const [logs, setLogs] = useState<Array<Log | string>>([]);
  const [errors, setErrors] = useState<ErrorDetails[]>([]);

  useEffect(() => {
    const pw = getPW();

    if (!pw) return;

    // Check the status right off the bat
    pw.checkStatus();

    pw.port.onmessage = (e: MessageEvent<LogsWorkerMessage>) => {
      const message = e.data;
      if (message.type === 'logsStatus') {
        setStatus(message.status);
        setSessionStart(message.sessionStart);
        setSessionEnd(message.sessionEnd);
        setErrors(message.errors);
      }

      if (message.type === 'allLogs') {
        setLogs(message.data);
      }

      if (message.type === 'logStreamData') {
        setLogs((prevLogs) => prevLogs.concat(message.data));
      }

      if (message.type === 'logStreamMalformedData') {
        setLogs((prevLogs) => prevLogs.concat(message.data));
      }

      if (message.type === 'subscribed') {
        setSubscribed(true);
        setStatus(message.status);
        setSessionStart(message.sessionStart);
        setSessionEnd(message.sessionEnd);
      }
    };

    // Disconnect when we unrender
    return () => {
      pw.disconnect();
    };
  }, [getPW]);

  // If we don't have any logs yet, request everything.
  // This could happen when returning to a page that has been streaming for a while
  useEffect(() => {
    const pw = getPW();

    if (!pw) return;

    if (!logs.length && !hasRequestedAllLogsRef.current) {
      pw.requestAllLogs();
      hasRequestedAllLogsRef.current = true;
    }

    // Automatically subscribe to log stream if it's already fetching
    if (status === 'fetching' && !isSubscribed) {
      pw.subscribe();
    }
  }, [isSubscribed, logs, status, getPW]);

  function startStream() {
    const pw = getPW();

    if (!pw) return;

    pw.subscribe();
    pw.startFetching();
  }

  return {
    startStream,
    status,
    sessionStart,
    sessionEnd,
    logs,
    errors,
  };
}
